import React, { FC, useEffect, useState } from 'react'
import { useAccount } from 'wagmi'
import useUsersAPI from '../../../__legacy__/api/v2/UsersAPI'
import { useAuth } from '../../hooks/useAuth'
import AuthStore from '../../stores/AuthStore'
import { Box } from '../core/Components'
import { Modal } from '../core/Modal'
import ConnectWallet from './ConnectWallet'
import CreateAccount from './CreateAccount'
import { SignMessage } from './SignMessage'
import { VerifyOnTwitter } from './VerifyOnTwitter'

export type AuthFlowPosition =
  | 'connectWallet'
  | 'signMessage'
  | 'createAccount'
  | 'verifyOnTwitter'
  | 'close'

interface Props {
  trigger?: JSX.Element
  initialFlowPosition?: AuthFlowPosition
  open?: boolean
  hideCloseButton?: boolean
  closeModal?: () => void
  onClickCloseModal?: () => void
  onSuccess?: () => void
}

/**
 * The authentication flow
 * Navigation of this set of modals is based on the flowPosition state
 * The overall modal is either in an controlled (open and closeModal are defined) or uncontrolled state (trigger is used)
 */
export const AuthFlowModal: FC<Props> = ({
  trigger,
  initialFlowPosition,
  open,
  closeModal,
  hideCloseButton,
  onClickCloseModal,
  onSuccess
}) => {
  const { setIsLoggingIn } = AuthStore
  const { address } = useAccount()
  const [forceCloseModal, setForceCloseModal] = useState(false)
  const [flowPosition, setFlowPosition] = useState<AuthFlowPosition>(
    initialFlowPosition ?? 'connectWallet'
  )
  const { getOneUser } = useUsersAPI()

  const onOpenChange = (open: boolean) => {
    setIsLoggingIn(open)
  }

  useEffect(() => {
    if (address)
      getOneUser(address).then((user) => {
        if (!user) return
        setFlowPosition('close')
      })
  }, [address])

  useEffect(() => {
    if (flowPosition === 'close') {
      closeModal?.()
      setForceCloseModal(true)
      setIsLoggingIn(false)
      setFlowPosition(initialFlowPosition ?? 'connectWallet')
      setForceCloseModal(false)
    }
  }, [flowPosition])

  const close = () => {
    if (flowPosition === 'verifyOnTwitter' && onSuccess) onSuccess()
    setFlowPosition('close')
    onClickCloseModal && onClickCloseModal()
  }

  return (
    <Modal
      open={forceCloseModal || open}
      onOpenChange={onOpenChange}
      onClickClose={() => close()}
      closeButtonCss={{ display: hideCloseButton ? 'none' : undefined }}
      trigger={trigger}
    >
      <AuthModalContent
        flowPosition={flowPosition}
        setFlowPosition={setFlowPosition}
        onSuccess={onSuccess}
      />
    </Modal>
  )
}

export const AuthModalContent = ({
  flowPosition,
  setFlowPosition,
  onSuccess
}: {
  flowPosition: AuthFlowPosition
  setFlowPosition: React.Dispatch<React.SetStateAction<AuthFlowPosition>>
  onSuccess?: () => void
}) => {
  const [emailOverload, setEmailOverload] = useState<string>(null)
  const renderFlowPosition = () => {
    switch (flowPosition) {
      case 'connectWallet':
        return (
          <ConnectWallet
            setFlowPosition={setFlowPosition}
            setEmailOverload={setEmailOverload}
            onSuccess={onSuccess}
          />
        )
      case 'signMessage':
        return <SignMessage setFlowPosition={setFlowPosition} />
      case 'createAccount':
        return <CreateAccount setFlowPosition={setFlowPosition} emailOverload={emailOverload} />
      case 'verifyOnTwitter':
        return <VerifyOnTwitter setFlowPosition={setFlowPosition} onSuccess={onSuccess} />
    }
  }

  return renderFlowPosition()
}

/**
 * A wrapper around an element that opens the login modal if they aren't logged in
 */
export const requiresUserLoggedIn = (e: JSX.Element) => {
  const { isUserLoading, user } = useAuth()

  if (!user || isUserLoading) {
    return <AuthFlowModal trigger={<Box>{e}</Box>} />
  }

  return e
}
